import {
  empty,
  failed,
  loading,
  ready,
  type ApiResult,
  type ScreenState,
  type TranslationKey,
} from "@planner/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { platform } from "../platform/index.js";
import { useToast } from "../shell/Toast.js";

/**
 * **تنبيهُ الجهاز — منطقٌ واحد لثلاثة مواضع.**
 *
 * فقسمُ البورتال وقسمُ «حسابي» وسؤالُ أوّلِ الدخول **يشتركون ويقطعون بنفس
 * الخطوات**: إذنُ الجهاز، ثم الاشتراكُ من المتصفّح، ثم حفظُه في السيرفر. وكانت
 * في البورتال وحده؛ **ونسخةٌ ثانيةٌ للموظّف تفترق يوماً** — جهازٌ يُشترَك عليه
 * من «حسابي» ولا يقطعه سؤالُ الدخول. والشكلُ وحده يختلف، بموك أب كلِّ موضع.
 *
 * **والمساراتُ تُكتَب في الشاشة لا هنا** — فكلُّ شاشةٍ تحمل زرَّها تسمّي طريقتَه
 * وعنوانَه، **وبوابةُ الأزرار تقرأ ذلك من ملفّها**. وعنوانٌ يُركَّب هنا من قاعدةٍ
 * كان يُخفي عنها كلَّ زرٍّ فيقول «بلا زرّ» وهو موجود.
 */

/** جهازٌ مفعَّل — **بلا عنوانه**، فهو سرُّ الجهاز ولا يخرج من السيرفر */
export type PushDevice = {
  readonly id: string;
  readonly deviceLabel: string;
  readonly at: string;
  readonly isThisDevice: boolean;
};

/** **والجسمُ بطريق الجهاز** — المتصفّحُ بمفتاحيه، وتطبيقُ أندرويد برمزه وحده */
type Subscription =
  | {
      readonly endpoint: string;
      readonly keys: { readonly p256dh: string; readonly auth: string };
      readonly deviceLabel: string;
    }
  | { readonly transport: "fcm"; readonly endpoint: string; readonly deviceLabel: string };

/** **ونداءاتُ الطرف** — تكتبها الشاشةُ بعنوانها وطريقتها */
export type PushRoutes = {
  readonly list?: (
    query: string,
    signal: AbortSignal,
  ) => Promise<ApiResult<{ readonly devices: readonly PushDevice[] }>>;
  readonly save: (body: Subscription) => Promise<ApiResult<unknown>>;
  /** **وسؤالُ الدخول لا يقطع** — فلا يُكتَب له نداءٌ لا زرَّ له */
  readonly drop?: (body: {
    readonly endpoint?: string;
    readonly id?: string;
  }) => Promise<ApiResult<unknown>>;
};

export type DevicePush = {
  /** هل يعرف المتصفّحُ التنبيهات أصلاً — وغيرُ المدعوم لا يُفتَح بشيء */
  readonly supported: boolean;
  /** **وغيرُ المضبوط شأنُ الإدارة** — لا يُعرَض له زرٌّ يفشل */
  readonly configured: boolean;
  /** هل سُئل المتصفّحُ عن اشتراك هذا الجهاز بعد — فلا يُرسَم «متوقّف» قبل أن يُعرَف */
  readonly known: boolean;
  /** عنوانُ اشتراكِ هذا الجهاز إن كان مفعَّلاً */
  readonly here: string | null;
  readonly devices: ScreenState<readonly PushDevice[]>;
  readonly busy: boolean;
  readonly problem: TranslationKey | null;
  enable(): Promise<boolean>;
  disable(): Promise<void>;
  remove(device: PushDevice): Promise<void>;
  reload(): void;
};

export function useDevicePush(publicKey: string, routes: PushRoutes): DevicePush {
  /** **والنداءاتُ تُقرأ من آخر رسمٍ** — فدالّةٌ جديدةٌ كلَّ رسمٍ لا تُعيد التحميل */
  const latest = useRef(routes);
  latest.current = routes;
  /** **وسؤالُ الدخول لا يقرأ القائمة** — يحتاج «هل هذا الجهازُ مفعَّل» وحده */
  const listed = routes.list !== undefined;

  const [here, setHere] = useState<string | null>(null);
  const [known, setKnown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<TranslationKey | null>(null);
  const [devices, setDevices] = useState<ScreenState<readonly PushDevice[]>>(loading);
  const [attempt, setAttempt] = useState(0);

  /**
   * **وكلُّ كتابةٍ تُقال نتيجتُها** — نجاحاً بتوستها المعلَن، وفشلاً بكوده.
   *
   * وكان البورتالُ يكتب ثم `if (!saved.ok) return` **بلا كلمة**: اشتراكٌ رفضه
   * السيرفر والزرُّ يعود كما كان، فيظنّ العميلُ أنه لم يضغط. وموضعُ القول هنا
   * مرّةً واحدة، فلا تنساه شاشة.
   */
  const toast = useToast();
  const told = useCallback(
    (result: ApiResult<unknown>): boolean => {
      if (!result.ok) {
        toast.failed(result.error);
        return false;
      }
      toast.succeeded(result.feedback ?? null, result.correlationId);
      return true;
    },
    [toast],
  );

  useEffect(() => {
    void platform.pushEndpoint().then((endpoint) => {
      setHere(endpoint);
      setKnown(true);
    });
  }, []);

  useEffect(() => {
    const list = latest.current.list;
    if (!listed || list === undefined) return;
    const controller = new AbortController();
    void (async () => {
      const query = here === null ? "" : `?endpoint=${encodeURIComponent(here)}`;
      const result = await list(query, controller.signal);
      if (controller.signal.aborted) return;
      if (!result.ok) {
        setDevices(failed(result.error));
        return;
      }
      setDevices(result.data.devices.length === 0 ? empty() : ready(result.data.devices));
    })();
    return () => {
      controller.abort();
    };
  }, [here, attempt, listed]);

  const reload = useCallback(() => {
    setDevices(loading);
    setAttempt((n) => n + 1);
  }, []);

  const enable = useCallback(async (): Promise<boolean> => {
    setBusy(true);
    setProblem(null);
    const subscribed = await platform.subscribePush(publicKey);
    if (!subscribed.ok) {
      setBusy(false);
      setProblem(subscribed.reason === "blocked" ? "push.blocked" : "push.unsupported");
      return false;
    }
    const saved = await latest.current.save(
      subscribed.transport === "fcm"
        ? { transport: "fcm", endpoint: subscribed.endpoint, deviceLabel: platform.deviceLabel() }
        : {
            endpoint: subscribed.endpoint,
            keys: { p256dh: subscribed.p256dh, auth: subscribed.auth },
            deviceLabel: platform.deviceLabel(),
          },
    );
    setBusy(false);
    if (!told(saved)) return false;
    setHere(subscribed.endpoint);
    return true;
  }, [publicKey, told]);

  const disable = useCallback(async (): Promise<void> => {
    const drop = latest.current.drop;
    if (drop === undefined) return;
    setBusy(true);
    const endpoint = (await platform.unsubscribePush()) ?? here ?? "";
    const removed = await drop({ endpoint });
    setBusy(false);
    if (!told(removed)) return;
    setHere(null);
  }, [here, told]);

  /**
   * **ويُقطَع من أي جهاز بمعرّفه** — فمن ضاع تليفونُه لا يملك عنوانَه، ولولا
   * القطعُ بالمعرّف لبقي التنبيهُ يصل إلى يدٍ غريبة.
   */
  const remove = useCallback(
    async (device: PushDevice): Promise<void> => {
      const drop = latest.current.drop;
      if (drop === undefined) return;
      setBusy(true);
      const removed = await drop({ id: device.id });
      setBusy(false);
      if (!told(removed)) return;
      if (device.isThisDevice) {
        await platform.unsubscribePush();
        setHere(null);
      }
      reload();
    },
    [reload, told],
  );

  return {
    supported: platform.pushSupported,
    configured: publicKey !== "",
    known,
    here,
    devices,
    busy,
    problem,
    enable,
    disable,
    remove,
    reload,
  };
}
